import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    X, MapPin, Shield, Clock, MessageSquare, Navigation,
    Award, Eye, EyeOff, Zap, AlertTriangle, Heart, Users,
    Trophy, Target, Radio, Trash2
} from 'lucide-react';
import { Contact, SafetyStatus } from '../types';
import { contactService } from '../services/contactService';

// --- BADGE DEFINITIONS ---
const BADGE_DEFINITIONS = [
    { id: 'first_alert', name: 'First Alert', icon: '🆘', description: 'Report your first hazard', tier: 'bronze', requirement: 1 },
    { id: 'watchdog', name: 'Watchdog', icon: '📡', description: 'Report 5 hazards', tier: 'silver', requirement: 5 },
    { id: 'guardian', name: 'Guardian', icon: '🛡️', description: 'Report 10 hazards', tier: 'gold', requirement: 10 },
    { id: 'lifesaver', name: 'Lifesaver', icon: '🏆', description: 'Help 50+ lives', tier: 'platinum', requirement: 50 },
    { id: 'connector', name: 'Connector', icon: '🤝', description: 'Add 5 contacts', tier: 'bronze', requirement: 5 },
    { id: 'communicator', name: 'Communicator', icon: '💬', description: 'Send 20 messages', tier: 'silver', requirement: 20 },
];

const TIER_COLORS: Record<string, string> = {
    bronze: '#CD7F32',
    silver: '#C0C0C0',
    gold: '#FFD700',
    platinum: '#E5E4E2'
};

interface ContactDetailModalProps {
    contact: Contact;
    onClose: () => void;
    onChat: () => void;
    onTrack: () => void;
    onPulse: () => void;
    onRemove: () => void;
    onViewProfile: () => void;
}

const ContactDetailModal: React.FC<ContactDetailModalProps> = ({
    contact,
    onClose,
    onChat,
    onTrack,
    onPulse,
    onRemove,
    onViewProfile
}) => {
    const [achievements, setAchievements] = useState<string[]>([]);
    const [stats, setStats] = useState({ reports: 0, livesHelped: 0, locationSharing: true });
    const [loading, setLoading] = useState(true);

    // Fetch contact details on mount
    useEffect(() => {
        const fetchDetails = async () => {
            try {
                const details = await contactService.getContactDetails(contact.id);
                if (details) {
                    setStats({
                        reports: details.reports_count || 0,
                        livesHelped: details.lives_helped || 0,
                        locationSharing: details.location_sharing ?? true
                    });
                }

                const userAchievements = await contactService.getUserAchievements(contact.id);
                setAchievements(userAchievements || []);
            } catch (e) {
                console.error("Failed to fetch contact details", e);
            } finally {
                setLoading(false);
            }
        };

        fetchDetails();
    }, [contact.id]);

    // Generate Day Summary based on time
    const getDaySummary = () => {
        const hour = new Date().getHours();
        const locationPart = contact.locationName || 'Unknown Location';

        if (hour < 6) return `🌙 Resting • Last seen near ${locationPart}`;
        if (hour < 12) return `☀️ Active this morning • ${locationPart}`;
        if (hour < 18) return `🌤️ Moving around • ${locationPart}`;
        return `🌆 Evening activity • ${locationPart}`;
    };

    const getStatusColor = (status: SafetyStatus) => {
        switch (status) {
            case SafetyStatus.SAFE:
            case SafetyStatus.HOME:
                return 'bg-green-500';
            case SafetyStatus.WARNING:
                return 'bg-yellow-500';
            case SafetyStatus.DANGER:
            case SafetyStatus.SOS:
                return 'bg-red-500';
            default:
                return 'bg-gray-500';
        }
    };

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[9999] bg-black/95 backdrop-blur-xl flex items-end md:items-center justify-center p-0 md:p-4"
                onClick={onClose}
            >
                <motion.div
                    initial={{ y: '100%', opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: '100%', opacity: 0 }}
                    transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                    onClick={(e) => e.stopPropagation()}
                    className="w-full max-w-lg bg-[#0a0f1a] border border-white/10 rounded-t-3xl md:rounded-2xl overflow-hidden max-h-[80vh] overflow-y-auto mb-[90px] md:mb-0"
                >
                    {/* HEADER */}
                    <div className="relative p-6 bg-gradient-to-b from-[#0f172a] to-transparent">
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
                        >
                            <X className="w-5 h-5 text-white" />
                        </button>

                        <div className="flex items-center gap-4">
                            <div className="relative">
                                <div className={`w-20 h-20 rounded-full border-4 p-0.5 ${getStatusColor(contact.status)}`}>
                                    <img
                                        src={contact.avatarUrl || `https://i.pravatar.cc/150?u=${contact.id}`}
                                        className="w-full h-full object-cover"
                                        alt={contact.name}
                                    />        </div>
                                <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full border-4 border-[#0a0f1a] ${getStatusColor(contact.status)}`} />
                            </div>

                            <div className="flex-1">
                                <h2 className="text-2xl font-bold text-white">{contact.name}</h2>
                                <div className="flex items-center gap-2 mt-1">
                                    <span className={`text-xs font-bold px-2 py-0.5 rounded ${getStatusColor(contact.status)} text-black`}>
                                        {contact.status}
                                    </span>
                                    <span className="text-xs text-gray-400">• {contact.lastSeen}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* DAY SUMMARY */}
                    <div className="px-6 py-4 border-b border-white/5">
                        <div className="flex items-center gap-3 text-gray-300">
                            <Clock className="w-5 h-5 text-[var(--neon-cyan)]" />
                            <span className="text-sm">{getDaySummary()}</span>
                        </div>
                    </div>

                    {/* LOCATION SHARING STATUS */}
                    <div className="px-6 py-4 border-b border-white/5">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                {stats.locationSharing ? (
                                    <Eye className="w-5 h-5 text-green-500" />
                                ) : (
                                    <EyeOff className="w-5 h-5 text-red-500" />
                                )}
                                <span className="text-sm text-gray-300">
                                    Location Sharing: {stats.locationSharing ? 'Enabled' : 'Hidden'}
                                </span>
                            </div>
                            <MapPin className="w-4 h-4 text-gray-500" />
                        </div>
                    </div>

                    {/* STATS */}
                    <div className="px-6 py-4 border-b border-white/5">
                        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">Activity Stats</h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="bg-white/5 rounded-xl p-4 text-center">
                                <AlertTriangle className="w-6 h-6 text-orange-500 mx-auto mb-2" />
                                <p className="text-2xl font-bold text-white">{stats.reports}</p>
                                <p className="text-xs text-gray-400">Hazards Reported</p>
                            </div>
                            <div className="bg-white/5 rounded-xl p-4 text-center">
                                <Heart className="w-6 h-6 text-red-500 mx-auto mb-2" />
                                <p className="text-2xl font-bold text-white">{stats.livesHelped}</p>
                                <p className="text-xs text-gray-400">Lives Helped</p>
                            </div>
                        </div>
                    </div>

                    {/* ACHIEVEMENTS */}
                    <div className="px-6 py-4 border-b border-white/5">
                        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3 flex items-center gap-2">
                            <Trophy className="w-4 h-4" />
                            Achievements
                        </h3>

                        {loading ? (
                            <div className="text-center py-4 text-gray-500">Loading...</div>
                        ) : (
                            <div className="grid grid-cols-3 gap-3">
                                {BADGE_DEFINITIONS.slice(0, 6).map((badge) => {
                                    const isEarned = achievements.includes(badge.id);
                                    return (
                                        <div
                                            key={badge.id}
                                            className={`p-3 rounded-xl text-center transition-all ${isEarned
                                                ? 'bg-white/10 border border-white/20'
                                                : 'bg-white/5 opacity-40'
                                                }`}
                                        >
                                            <span className="text-2xl">{badge.icon}</span>
                                            <p className="text-[10px] font-bold text-white mt-1 truncate">{badge.name}</p>
                                            {isEarned && (
                                                <div
                                                    className="w-3 h-3 rounded-full mx-auto mt-1"
                                                    style={{ backgroundColor: TIER_COLORS[badge.tier] }}
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* QUICK ACTIONS */}
                    <div className="p-6">
                        <div className="grid grid-cols-4 gap-2">
                            <button
                                onClick={onChat}
                                className="flex flex-col items-center gap-2 p-3 bg-[var(--neon-cyan)]/10 border border-[var(--neon-cyan)]/30 rounded-xl hover:bg-[var(--neon-cyan)]/20 transition-colors"
                            >
                                <MessageSquare className="w-5 h-5 text-[var(--neon-cyan)]" />
                                <span className="text-[10px] font-bold text-[var(--neon-cyan)]">CHAT</span>
                            </button>
                            <button
                                onClick={onPulse}
                                className="flex flex-col items-center gap-2 p-3 bg-green-500/10 border border-green-500/30 rounded-xl hover:bg-green-500/20 transition-colors"
                            >
                                <Radio className="w-5 h-5 text-green-500" />
                                <span className="text-[10px] font-bold text-green-500">PULSE</span>
                            </button>
                            <button
                                onClick={onTrack}
                                className="flex flex-col items-center gap-2 p-3 bg-purple-500/10 border border-purple-500/30 rounded-xl hover:bg-purple-500/20 transition-colors"
                            >
                                <Navigation className="w-5 h-5 text-purple-500" />
                                <span className="text-[10px] font-bold text-purple-500">TRACK</span>
                            </button>
                            <button
                                onClick={onViewProfile}
                                className="flex flex-col items-center gap-2 p-3 bg-blue-500/10 border border-blue-500/30 rounded-xl hover:bg-blue-500/20 transition-colors"
                            >
                                <Users className="w-5 h-5 text-blue-500" />
                                <span className="text-[10px] font-bold text-blue-500">PROFILE</span>
                            </button>
                        </div>

                        {/* REMOVE CONTACT */}
                        <div className="mt-4 pt-4 border-t border-white/5">
                            <button
                                onClick={() => {
                                    if (confirm('Are you sure you want to remove this contact?')) {
                                        onRemove();
                                    }
                                }}
                                className="w-full py-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 font-semibold text-sm hover:bg-red-500/20 transition-all flex items-center justify-center gap-2"
                            >
                                <Trash2 className="w-4 h-4" />
                                Remove Contact
                            </button>
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default ContactDetailModal;
